import { useState } from 'react'

const SYSTEM_TYPE_LABELS: Record<string, string> = {
    in_scope: 'In Scope',
    hr: 'HR',
    ticket: 'Ticket',
}

const hasPair = (rules: any[], typeA: string, typeB: string): boolean => {
    // Accept both 'main' and 'in_scope' as equivalent
    const matchesKey = (key: string, target: string) =>
        key === target || (target === 'in_scope' && key === 'main') || (target === 'main' && key === 'in_scope')

    return rules.some((rule: any) => {
        const keys = Object.keys(rule)
        return keys.some(k => matchesKey(k, typeA)) && keys.some(k => matchesKey(k, typeB))
    })
}

/**
 * Validates that the consolidation config has the required pairings.
 * - If HR system is selected: at least one In Scope ↔ HR rule
 * - If Ticket system is selected: at least one rule pairing Ticket with either In Scope or HR
 * Returns an error string or null if valid.
 */
export const validateConsolidationPairings = (jsonStr: string, hasHrSelected: boolean, hasTicketSelected: boolean): string | null => {
    try {
        const parsed = normalizeConfig(JSON.parse(jsonStr))
        if (parsed.length === 0) return 'Consolidation config must have at least one rule'
        if (hasHrSelected && !hasPair(parsed, 'in_scope', 'hr')) return 'Missing required In Scope ↔ HR matching rule'
        if (hasTicketSelected) {
            const hasTicketPair = hasPair(parsed, 'in_scope', 'ticket') || hasPair(parsed, 'hr', 'ticket')
            if (!hasTicketPair) return 'Missing required Ticket matching rule (pair with In Scope or HR)'
        }
        return null
    } catch {
        return 'Consolidation config must be valid JSON'
    }
}

interface ConsolidationLine {
    systemType1: string
    key1: string
    systemType2: string
    key2: string
}

interface ConsolidationConfigBuilderProps {
    value: string
    onChange: (value: string) => void
    availableSystemTypes: string[]
    error?: string
}

// Map SystemTypes.IN_SCOPE ('main') to the consolidation JSON key ('in_scope')
const toConsolidationKey = (systemType: string): string => systemType === 'main' ? 'in_scope' : systemType

const emptyLine = (systemTypes: string[]): ConsolidationLine => {
    const types = systemTypes.map(toConsolidationKey)
    const t1 = types.includes('in_scope') ? 'in_scope' : types[0] || 'in_scope'
    const t2 = types.includes('hr') ? 'hr' : types.find(t => t !== t1) || (t1 === 'in_scope' ? 'hr' : 'in_scope')
    return { systemType1: t1, key1: '', systemType2: t2, key2: '' }
}

const normalizeConfig = (parsed: any): any[] => {
    if (Array.isArray(parsed)) return parsed
    if (parsed && typeof parsed === 'object') return [parsed]
    return []
}

const jsonToLines = (jsonStr: string, systemTypes: string[]): ConsolidationLine[] => {
    try {
        const parsed = normalizeConfig(JSON.parse(jsonStr))
        if (parsed.length === 0) return [emptyLine(systemTypes)]
        return parsed.map((item: any) => {
            const keys = Object.keys(item)
            if (keys.length < 2) return emptyLine(systemTypes)
            return {
                systemType1: toConsolidationKey(keys[0]),
                key1: Array.isArray(item[keys[0]]) ? item[keys[0]].join(', ') : String(item[keys[0]] || ''),
                systemType2: toConsolidationKey(keys[1]),
                key2: Array.isArray(item[keys[1]]) ? item[keys[1]].join(', ') : String(item[keys[1]] || ''),
            }
        })
    } catch {
        return [emptyLine(systemTypes)]
    }
}

const linesToJson = (lines: ConsolidationLine[]): string => {
    const arr = lines
        .filter(l => l.systemType1 && l.key1 && l.systemType2 && l.key2)
        .map(l => ({
            [l.systemType1]: l.key1.split(',').map(k => k.trim()).filter(Boolean),
            [l.systemType2]: l.key2.split(',').map(k => k.trim()).filter(Boolean),
        }))
    return JSON.stringify(arr, null, 4)
}

const linesToPreviewJson = (lines: ConsolidationLine[]): string => {
    const arr = lines.map(l => {
        const t1 = l.systemType1 || 'in_scope'
        const t2 = l.systemType2 || (t1 === 'in_scope' ? 'hr' : 'in_scope')
        return {
            [t1]: l.key1 ? l.key1.split(',').map(k => k.trim()).filter(Boolean) : ['<empty>'],
            [t2]: l.key2 ? l.key2.split(',').map(k => k.trim()).filter(Boolean) : ['<empty>'],
        }
    })
    return JSON.stringify(arr, null, 4)
}


export const ConsolidationConfigBuilder: React.FC<ConsolidationConfigBuilderProps> = ({
    value, onChange, availableSystemTypes, error
}) => {
    const hasTicket = availableSystemTypes.includes('ticket')
    const [mode, setMode] = useState<'builder' | 'json'>('builder')
    const [lines, setLines] = useState<ConsolidationLine[]>(() => {
        if (!value || value === '[]') return [emptyLine(availableSystemTypes)]
        return jsonToLines(value, availableSystemTypes)
    })
    const [jsonText, setJsonText] = useState(value || '[]')
    const [jsonError, setJsonError] = useState('')

    const updateLines = (newLines: ConsolidationLine[]) => {
        setLines(newLines)
        onChange(linesToJson(newLines))
    }

    const switchToBuilder = () => {
        const newLines = jsonToLines(jsonText, availableSystemTypes)
        setLines(newLines)
        onChange(linesToJson(newLines))
        setMode('builder')
    }

    const switchToJson = () => {
        const json = linesToJson(lines)
        setJsonText(json)
        setJsonError('')
        setMode('json')
    }

    const updateLine = (index: number, field: keyof ConsolidationLine, val: string) => {
        const newLines = lines.map((l, i) => {
            if (i !== index) return l
            const updated = { ...l, [field]: val }
            // If both dropdowns end up the same, swap the other one
            if (field === 'systemType1' && updated.systemType2 === val) {
                updated.systemType2 = allRuleTypes.find(t => t !== val) || ''
            }
            if (field === 'systemType2' && updated.systemType1 === val) {
                updated.systemType1 = allRuleTypes.find(t => t !== val) || ''
            }
            return updated
        })
        updateLines(newLines)
    }

    const addLine = () => {
        updateLines([...lines, emptyLine(availableSystemTypes)])
    }

    const removeLine = (index: number) => {
        if (lines.length <= 1) return
        updateLines(lines.filter((_, i) => i !== index))
    }

    const onJsonBlur = () => {
        try {
            const parsed = JSON.parse(jsonText)
            if (!Array.isArray(parsed)) {
                setJsonError('Must be a JSON array')
                return
            }
            setJsonError('')
            const formatted = JSON.stringify(parsed, null, 4)
            setJsonText(formatted)
            onChange(formatted)
        } catch {
            setJsonError('Invalid JSON')
        }
    }

    // Map incoming system types to consolidation JSON keys
    const consolidationTypes = availableSystemTypes.map(toConsolidationKey)
    const hasInScope = consolidationTypes.includes('in_scope')
    const hasHr = consolidationTypes.includes('hr')

    const allRuleTypes = hasInScope && hasHr
        ? (hasTicket ? ['in_scope', 'hr', 'ticket'] : ['in_scope', 'hr'])
        : consolidationTypes

    const systemTypeOptions = allRuleTypes.map(st => ({
        value: st,
        label: SYSTEM_TYPE_LABELS[st] || st,
    }))

    return (
        <div className="consolidation-builder">
            <div className="builder-mode-toggle">
                <button
                    type="button"
                    className={mode === 'builder' ? 'active' : ''}
                    onClick={switchToBuilder}
                >Builder</button>
                <button
                    type="button"
                    className={mode === 'json' ? 'active' : ''}
                    onClick={switchToJson}
                >JSON</button>
            </div>

            {mode === 'builder' && (<>
                <div className="builder-lines">
                    {lines.map((line, i) => {
                        // Filter out the selected type1 from type2 options (can't match same type)
                        const type2Opts = systemTypeOptions.filter(o => o.value !== line.systemType1)
                        return (
                            <div key={i} className="builder-line">
                                <select
                                    value={line.systemType1}
                                    onChange={e => updateLine(i, 'systemType1', e.target.value)}
                                >
                                    {systemTypeOptions.map(o => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                                <input
                                    type="text"
                                    placeholder="matching key(s)"
                                    value={line.key1}
                                    onChange={e => updateLine(i, 'key1', e.target.value)}
                                />
                                <span className="line-connector">↔</span>
                                <select
                                    value={line.systemType2}
                                    onChange={e => updateLine(i, 'systemType2', e.target.value)}
                                >
                                    {type2Opts.map(o => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                                <input
                                    type="text"
                                    placeholder="matching key(s)"
                                    value={line.key2}
                                    onChange={e => updateLine(i, 'key2', e.target.value)}
                                />
                                <button
                                    type="button"
                                    className="remove-line-btn"
                                    onClick={() => removeLine(i)}
                                    disabled={lines.length <= 1}
                                    title="Remove line"
                                >×</button>
                            </div>
                        )
                    })}
                    <button type="button" className="add-line-btn" onClick={addLine}>
                        + Add Matching Rule
                    </button>
                </div>
                <div className="builder-preview">
                    <span className="preview-label">JSON Preview</span>
                    <pre>{linesToPreviewJson(lines)}</pre>
                </div>
            </>)}

            {mode === 'json' && (
                <div className="builder-json">
                    <textarea
                        value={jsonText}
                        onChange={e => { setJsonText(e.target.value); setJsonError('') }}
                        onBlur={onJsonBlur}
                        rows={8}
                        spellCheck={false}
                        className={jsonError ? 'textarea-error' : ''}
                    />
                    {jsonError && <span className="field-error">{jsonError}</span>}
                </div>
            )}

            {error && <span className="field-error">{error}</span>}
        </div>
    )
}
